import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import { usePermission } from '../context/PermissionContext';

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('ko-KR', { month:'2-digit', day:'2-digit' }) : '-';
const fmtDateTime = (d) => d ? new Date(d).toLocaleString('ko-KR', { month:'2-digit', day:'2-digit', hour:'2-digit', minute:'2-digit' }) : '-';

export default function ApprovalsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.uid === 'U-0000';
  const { canPerformAction } = usePermission();
  const canApprove = canPerformAction('승인 처리');
  const [activeTab, setActiveTab] = useState('pending');
  const [pending, setPending] = useState([]);
  const [completed, setCompleted] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [shareRequests, setShareRequests] = useState([]);
  const [srRejectTarget, setSrRejectTarget] = useState(null);
  const [srRejectReason, setSrRejectReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState({});
  const [search, setSearch] = useState('');

  // 모달
  const [approveTarget, setApproveTarget] = useState(null); // id 또는 [ids]
  const [rejectTarget, setRejectTarget] = useState(null);
  const [password, setPassword] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 3000); };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [p, c, m, sr] = await Promise.all([
        api.get('/approvals/pending'),
        api.get('/approvals/completed'),
        api.get('/approvals/my-requests'),
        api.get('/share-requests/incoming').catch(()=>[]),
      ]);
      setPending(p); setCompleted(c); setMyRequests(m); setShareRequests(sr);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // 검색 필터
  const filter = (list) => {
    if (!search) return list;
    const q = search.toLowerCase();
    return list.filter(r =>
      (r.requester_name || '').toLowerCase().includes(q) ||
      (r.receiver_company || '').toLowerCase().includes(q) ||
      (r.project_name || '').toLowerCase().includes(q) ||
      (r.reason || '').toLowerCase().includes(q)
    );
  };

  // 체크박스
  const filteredPending = filter(pending);
  const allSelected = filteredPending.length > 0 && filteredPending.every(r => selected[r.id]);
  const someSelected = filteredPending.some(r => selected[r.id]);
  const selectedCount = Object.values(selected).filter(Boolean).length;
  const toggleAll = () => { if (allSelected) setSelected({}); else setSelected(Object.fromEntries(filteredPending.map(r => [r.id, true]))); };
  const toggleOne = (id) => setSelected(prev => ({ ...prev, [id]: !prev[id] }));

  // 승인 처리
  const doApprove = async () => {
    if (!password) return;
    setProcessing(true);
    try {
      const ids = Array.isArray(approveTarget) ? approveTarget : [approveTarget];
      let lastMsg = '';
      for (const id of ids) {
        const res = await api.post(`/approvals/${id}/approve`, { password });
        lastMsg = res.message;
      }
      showToast(lastMsg || '승인 처리되었습니다.');
      setApproveTarget(null); setPassword(''); setSelected({});
      loadData();
    } catch (e) { alert(e.message); }
    finally { setProcessing(false); }
  };

  // 반려 처리
  const doReject = async () => {
    if (!rejectReason) return;
    setProcessing(true);
    try {
      const ids = Array.isArray(rejectTarget) ? rejectTarget : [rejectTarget];
      for (const id of ids) {
        await api.post(`/approvals/${id}/reject`, { reject_reason: rejectReason });
      }
      showToast('반려 처리되었습니다.');
      setRejectTarget(null); setRejectReason(''); setSelected({});
      loadData();
    } catch (e) { alert(e.message); }
    finally { setProcessing(false); }
  };

  // 승인 경로 표시
  const ApprovalPath = ({ path }) => {
    if (!path || !path.length) return <span className="text-muted">-</span>;
    return (
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
        {path.map((p, i) => (
          <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 2, fontSize: 10 }}>
            <span className={`badge ${p.status === 'approved' ? 'badge-green' : p.status === 'pending' ? 'badge-yellow' : 'badge-gray'}`} style={{ fontSize: 9 }}>
              {p.approver_name}({p.org_name})
            </span>
            {i < path.length - 1 && <span style={{ color: '#94a3b8' }}>→</span>}
          </span>
        ))}
      </div>
    );
  };

  // trigger_items 표시
  const TriggerInfo = ({ items }) => {
    if (!items || !items.length) return null;
    return (
      <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>
        {items.map((t, i) => <div key={i}>· {t.product || t.item_name}: {t.reason}</div>)}
      </div>
    );
  };

  return (
    <div>
      {toast && <div style={{ position: 'fixed', top: 20, left: '50%', transform: 'translateX(-50%)', zIndex: 9999, background: '#1e293b', color: '#fff', padding: '10px 24px', borderRadius: 8, fontSize: 13, fontWeight: 600, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>{toast}</div>}

      {/* Tabs */}
      <div className="tabs">
        {[
          { key: 'pending', label: `승인 대기 (${pending.length})` },
          { key: 'completed', label: `처리 완료 (${completed.length})` },
          { key: 'myRequests', label: `내가 요청한 (${myRequests.length})` },
          { key: 'shareRequests', label: `공유 요청 (${shareRequests.length})` },
        ].map(t => (
          <div key={t.key} className={`tab ${activeTab === t.key ? 'active' : ''}`} onClick={() => { setActiveTab(t.key); setSelected({}); }}>
            {t.label}
          </div>
        ))}
      </div>

      {/* Search + Bulk Actions */}
      <div className="toolbar">
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <div className="search-box">
            <span>🔍</span>
            <input placeholder="요청자, 수신처, 사업명 검색..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
        </div>
        {!isAdmin && activeTab === 'pending' && someSelected && (
          <div className="btn-group">
            {canApprove && <button className="btn btn-primary btn-sm" onClick={() => setApproveTarget(Object.keys(selected).filter(k => selected[k]).map(Number))}>✓ 일괄 승인 ({selectedCount}건)</button>}
            {canApprove && <button className="btn btn-danger btn-sm" onClick={() => setRejectTarget(Object.keys(selected).filter(k => selected[k]).map(Number))}>✕ 일괄 반려 ({selectedCount}건)</button>}
          </div>
        )}
      </div>

      {loading && <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>로딩 중...</div>}

      {/* ===== PENDING TAB ===== */}
      {!loading && activeTab === 'pending' && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  {!isAdmin && <th style={{ width: 36 }}><input type="checkbox" checked={allSelected} onChange={toggleAll} /></th>}
                  <th>요청일</th>
                  <th>요청자</th>
                  <th>견적번호</th>
                  <th>수신처</th>
                  <th>사업명</th>
                  <th>승인 사유</th>
                  <th>승인 경로</th>
                  {!isAdmin && <th>처리</th>}
                </tr>
              </thead>
              <tbody>
                {filteredPending.map(r => (
                  <tr key={r.id}>
                    {!isAdmin && <td><input type="checkbox" checked={!!selected[r.id]} onChange={() => toggleOne(r.id)} /></td>}
                    <td>{fmtDate(r.requested_at)}</td>
                    <td>{r.requester_name}<br /><span className="text-muted">{r.requester_dept}</span></td>
                    <td><span style={{ cursor: 'pointer', color: '#3b82f6', fontWeight: 600 }} onClick={() => navigate(`/product-estimate/new?id=${r.estimate_id}`)}>{r.estimate_number || r.draft_id}</span></td>
                    <td>{r.receiver_company}</td>
                    <td>{r.project_name}</td>
                    <td style={{ maxWidth: 200 }}>
                      {r.reason}
                      <TriggerInfo items={r.trigger_items} />
                    </td>
                    <td><ApprovalPath path={r.approval_path} /></td>
                    {!isAdmin && <td>
                      <div className="btn-group">
                        {canApprove && <button className="btn btn-primary btn-sm" onClick={() => setApproveTarget(r.id)}>승인</button>}
                        {canApprove && <button className="btn btn-danger btn-sm" onClick={() => setRejectTarget(r.id)}>반려</button>}
                      </div>
                    </td>}
                  </tr>
                ))}
                {filteredPending.length === 0 && <tr><td colSpan={9} style={{ textAlign: 'center', padding: 32, color: '#94a3b8' }}>승인 대기 건이 없습니다.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ===== COMPLETED TAB ===== */}
      {!loading && activeTab === 'completed' && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>요청일</th>
                  <th>요청자</th>
                  <th>견적번호</th>
                  <th>수신처</th>
                  <th>사업명</th>
                  <th>승인 사유</th>
                  <th>결과</th>
                  <th>처리일</th>
                  <th>승인 경로</th>
                </tr>
              </thead>
              <tbody>
                {filter(completed).map(r => (
                  <tr key={r.id}>
                    <td>{fmtDate(r.requested_at)}</td>
                    <td>{r.requester_name}<br /><span className="text-muted">{r.requester_dept}</span></td>
                    <td><span style={{ cursor: 'pointer', color: '#3b82f6', fontWeight: 600 }} onClick={() => navigate(`/product-estimate/new?id=${r.estimate_id}`)}>{r.estimate_number || r.draft_id}</span></td>
                    <td>{r.receiver_company}</td>
                    <td>{r.project_name}</td>
                    <td style={{ maxWidth: 200 }}>{r.reason}</td>
                    <td>
                      <span className={`badge ${r.status === 'approved' ? 'badge-green' : 'badge-red'}`}>{r.status === 'approved' ? '승인' : '반려'}</span>
                      {r.reject_reason && <div className="text-muted" style={{ marginTop: 2, fontSize: 11 }}>{r.reject_reason}</div>}
                    </td>
                    <td>{fmtDateTime(r.completed_at)}</td>
                    <td><ApprovalPath path={r.approval_path} /></td>
                  </tr>
                ))}
                {filter(completed).length === 0 && <tr><td colSpan={9} style={{ textAlign: 'center', padding: 32, color: '#94a3b8' }}>처리 완료 건이 없습니다.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ===== MY REQUESTS TAB ===== */}
      {!loading && activeTab === 'myRequests' && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>요청일</th>
                  <th>견적번호</th>
                  <th>수신처</th>
                  <th>사업명</th>
                  <th>승인 사유</th>
                  <th>상태</th>
                  <th>승인 경로</th>
                </tr>
              </thead>
              <tbody>
                {filter(myRequests).map(r => (
                  <tr key={r.id}>
                    <td>{fmtDate(r.requested_at)}</td>
                    <td><span style={{ cursor: 'pointer', color: '#3b82f6', fontWeight: 600 }} onClick={() => navigate(`/product-estimate/new?id=${r.estimate_id}`)}>{r.estimate_number || r.draft_id}</span></td>
                    <td>{r.receiver_company}</td>
                    <td>{r.project_name}</td>
                    <td style={{ maxWidth: 200 }}>
                      {r.reason}
                      {r.reject_reason && <div style={{ color: '#ef4444', fontSize: 11, marginTop: 2 }}>반려: {r.reject_reason}</div>}
                    </td>
                    <td>
                      <span className={`badge ${r.status === 'pending' ? 'badge-yellow' : r.status === 'approved' ? 'badge-green' : r.status === 'rejected' ? 'badge-red' : 'badge-gray'}`}>
                        {r.status === 'pending' ? '승인대기' : r.status === 'approved' ? '승인완료' : r.status === 'rejected' ? '반려' : '취소'}
                      </span>
                    </td>
                    <td><ApprovalPath path={r.approval_path} /></td>
                  </tr>
                ))}
                {filter(myRequests).length === 0 && <tr><td colSpan={7} style={{ textAlign: 'center', padding: 32, color: '#94a3b8' }}>요청한 건이 없습니다.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ===== SHARE REQUESTS TAB ===== */}
      {!loading && activeTab === 'shareRequests' && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>요청일</th>
                  <th>견적번호</th>
                  <th>수신처</th>
                  <th>사업명</th>
                  <th>요청자</th>
                  <th>메시지</th>
                  <th>작업</th>
                </tr>
              </thead>
              <tbody>
                {shareRequests.map(r => (
                  <tr key={r.id}>
                    <td>{fmtDate(r.requested_at)}</td>
                    <td><span style={{ cursor: 'pointer', color: '#3b82f6', fontWeight: 600 }} onClick={() => navigate(`/product-estimate/new?id=${r.estimate_id}`)}>{r.estimate_number || r.draft_id}</span></td>
                    <td>{r.receiver_company || '-'}</td>
                    <td>{r.project_name || '-'}</td>
                    <td>{r.requester_name} <span style={{ color: '#94a3b8', fontSize: 11 }}>({r.requester_uid})</span></td>
                    <td style={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.message || '-'}</td>
                    <td>
                      <div className="btn-group">
                        <button className="btn btn-primary btn-sm" onClick={async () => {
                          if (!window.confirm(`${r.requester_name} 님에게 견적서 ${r.estimate_number || r.draft_id} 열람 권한을 부여합니다. 승인할까요?`)) return;
                          try { await api.post(`/share-requests/${r.id}/approve`); showToast('승인되었습니다.'); loadData(); }
                          catch (e) { alert(e.message); }
                        }}>승인</button>
                        <button className="btn btn-danger btn-sm" onClick={() => { setSrRejectTarget(r); setSrRejectReason(''); }}>반려</button>
                      </div>
                    </td>
                  </tr>
                ))}
                {shareRequests.length === 0 && <tr><td colSpan={7} style={{ textAlign: 'center', padding: 32, color: '#94a3b8' }}>대기 중인 공유 요청이 없습니다.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ===== SHARE REQUEST REJECT MODAL ===== */}
      {srRejectTarget && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setSrRejectTarget(null)}>
          <div onClick={ev => ev.stopPropagation()} style={{ background: '#fff', borderRadius: 14, padding: 24, width: 400 }}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 12, paddingBottom: 10, borderBottom: '1px solid #e2e8f0' }}>공유 요청 반려</div>
            <div style={{ fontSize: 13, marginBottom: 12, color: '#475569' }}>
              {srRejectTarget.requester_name} 님의 {srRejectTarget.estimate_number || srRejectTarget.draft_id} 요청을 반려합니다.
            </div>
            <div className="form-group">
              <label className="form-label">반려 사유 (선택)</label>
              <textarea className="form-input" rows={3} value={srRejectReason} onChange={e=>setSrRejectReason(e.target.value)} />
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-outline" onClick={() => setSrRejectTarget(null)}>취소</button>
              <button className="btn btn-danger" onClick={async () => {
                try { await api.post(`/share-requests/${srRejectTarget.id}/reject`, { reject_reason: srRejectReason }); showToast('반려되었습니다.'); setSrRejectTarget(null); loadData(); }
                catch (e) { alert(e.message); }
              }}>반려</button>
            </div>
          </div>
        </div>
      )}

      {/* ===== APPROVE MODAL ===== */}
      {approveTarget && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: 28, width: 400 }}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, paddingBottom: 10, borderBottom: '1px solid #e2e8f0' }}>승인 확인</div>
            <div style={{ fontSize: 13, marginBottom: 16, color: '#475569' }}>
              {Array.isArray(approveTarget) ? `${approveTarget.length}건을 승인합니다.` : '이 건을 승인합니다.'} 비밀번호를 입력하세요.
            </div>
            <div className="form-group">
              <label className="form-label">비밀번호</label>
              <input className="form-input" type="password" placeholder="현재 계정 비밀번호" value={password}
                onChange={e => setPassword(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && password) doApprove(); }} />
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-outline" onClick={() => { setApproveTarget(null); setPassword(''); }}>취소</button>
              <button className="btn btn-primary" disabled={!password || processing} onClick={doApprove}>{processing ? '처리 중...' : '승인'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ===== REJECT MODAL ===== */}
      {rejectTarget && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: 28, width: 460 }}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, paddingBottom: 10, borderBottom: '1px solid #e2e8f0' }}>반려 사유 입력</div>
            <div style={{ fontSize: 13, marginBottom: 16, color: '#475569' }}>
              {Array.isArray(rejectTarget) ? `${rejectTarget.length}건을 반려합니다.` : '이 건을 반려합니다.'} 사유를 입력해 주세요.
            </div>
            <div className="form-group">
              <label className="form-label">반려 사유 (필수)</label>
              <textarea className="form-input" rows={4} placeholder="반려 사유를 입력하세요..." value={rejectReason}
                onChange={e => setRejectReason(e.target.value)} />
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-outline" onClick={() => { setRejectTarget(null); setRejectReason(''); }}>취소</button>
              <button className="btn btn-danger" disabled={!rejectReason || processing} style={{ background: '#ef4444', color: '#fff', border: 'none', opacity: rejectReason ? 1 : 0.5 }}
                onClick={doReject}>{processing ? '처리 중...' : '반려'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
